// KVKK storage notice (mockup market/footer.jsx CookieConsent). The site loads
// no analytics or marketing scripts, so the mockup's analytics/marketing
// toggles had nothing to switch: offering them implied tracking that does not
// exist. The banner is an essential-storage notice with one acknowledge
// action; re-add consent categories only together with a script they gate.
// The acknowledgement persists client-side in localStorage (no backend); the
// banner shows on first visit and stays hidden once a choice is stored.
//
// Accessibility: the banner is a role="dialog" with a label, a simple focus
// move on mount, and Escape collapses it to the re-open pill (no choice is
// forced — the visitor can reopen).

import { ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

const STORAGE_KEY = "novem.cookie-consent.v1";

export interface CookieConsentValue {
  readonly essential: true;
  readonly decidedAt: string;
}

/** Read the stored choice; null when the visitor has not decided yet. */
function readStored(): CookieConsentValue | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    // Older stored choices also carry analytics/marketing booleans; their
    // `essential: true` still marks the notice as seen.
    const parsed = JSON.parse(raw) as Partial<CookieConsentValue> | null;
    if (parsed === null || parsed.essential !== true) return null;
    return {
      essential: true,
      decidedAt: typeof parsed.decidedAt === "string" ? parsed.decidedAt : new Date().toISOString(),
    };
  } catch {
    // Corrupt/blocked storage → treat as undecided (banner shows).
    return null;
  }
}

function writeStored(value: CookieConsentValue): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable (private mode / blocked) — the banner simply
    // reappears next load; nothing else to do.
  }
}

export function CookieConsent(): React.ReactElement | null {
  const { t } = useTranslation();
  // undefined = not yet read from storage (first render before effect).
  const [decided, setDecided] = useState<boolean | undefined>(undefined);
  const [visible, setVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // First visit detection runs once, after mount (localStorage is client-only).
  useEffect(() => {
    const stored = readStored();
    setDecided(stored !== null);
    setVisible(stored === null);
  }, []);

  // Move focus into the banner when it appears so keyboard users land on it.
  useEffect(() => {
    if (visible && panelRef.current) {
      panelRef.current.focus();
    }
  }, [visible]);

  const acknowledge = useCallback((): void => {
    writeStored({ essential: true, decidedAt: new Date().toISOString() });
    setDecided(true);
    setVisible(false);
  }, []);

  // Escape collapses the banner to the re-open pill (no forced choice).
  const onKeyDown = useCallback((e: React.KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setVisible(false);
    }
  }, []);

  // Still reading storage — render nothing to avoid a flash on decided visits.
  if (decided === undefined) return null;

  if (!visible) {
    return (
      <button
        type="button"
        onClick={(): void => setVisible(true)}
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-[12px] border border-[var(--border-subtle)] bg-[var(--bg-raised)] px-4 py-2.5 font-sans text-[13px] text-[var(--fg-secondary)] shadow-[var(--shadow-sm)] hover:text-[var(--fg-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
      >
        <ShieldCheck size={15} aria-hidden="true" /> {t("cookie.reopen")}
      </button>
    );
  }

  return (
    // Non-modal dismissible dialog: the Escape handler needs the container to
    // listen for keys; every action inside is a real <button>/<Link> and the
    // dialog itself is focusable (tabIndex), so the handler is reachable.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label={t("cookie.dialog_label")}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className="fixed bottom-6 left-6 z-50 w-[min(432px,calc(100vw-2rem))] rounded-[18px] border border-[var(--border-subtle)] bg-[var(--bg-raised)] p-6 shadow-[var(--shadow-xl)] focus:outline-none"
    >
      <div className="mb-4 flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-[var(--mkt-panel)]">
          <ShieldCheck size={20} aria-hidden="true" className="text-[var(--accent)]" />
        </div>
        <div>
          <p className="mb-1.5 font-serif text-[17px] font-medium text-[var(--fg-primary)]">
            {t("cookie.title")}
          </p>
          <p className="m-0 font-serif text-[13.5px] leading-snug text-[var(--fg-secondary)]">
            {t("cookie.body")}{" "}
            <Link
              to="/legal?doc=cookies"
              className="text-[var(--accent-hover)] underline focus:outline-none focus-visible:rounded-[3px] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
            >
              {t("cookie.policy_link")}
            </Link>
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={acknowledge}
        className="w-full rounded-[9px] bg-[var(--accent)] px-3 py-3 font-sans text-sm font-semibold text-[var(--fg-on-accent)] hover:bg-[var(--accent-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
      >
        {t("cookie.acknowledge")}
      </button>
    </div>
  );
}
